import { toPillarApiError } from "shared/api/PillarApiError";
import PillarForm from "shared/components/pillar-form/PillarForm";
import { usePillarTableContext } from "shared/components/pillar-table/PillarTableContext";
import { type PillarTableSafeAny } from "shared/components/pillar-table/PillarTableSafeAny";
import { usePillarTableRowContext } from "shared/components/pillar-table/row/PillarTableRowContext";

const PillarTableRowInlineEdit = <
  RowGeneric = PillarTableSafeAny,
  ExtraPropsGeneric = PillarTableSafeAny | undefined,
  InlineEditFormGeneric = PillarTableSafeAny | undefined,
>() => {
  const { testidTable, handleInlineEdit, refetchData } = usePillarTableContext<
    RowGeneric,
    ExtraPropsGeneric,
    InlineEditFormGeneric
  >();
  const {
    rowIndex,
    inlineEditMode,
    setInlineEditMode,
    rowValue,
    formValues,
    setRowValue,
    setRowActionFailure,
  } = usePillarTableRowContext<
    RowGeneric,
    ExtraPropsGeneric,
    InlineEditFormGeneric
  >();
  if (handleInlineEdit && inlineEditMode === "none") {
    return (
      <td
        data-testid={`${testidTable}-table-body-row-column-inlineEdit`}
        key={`row-${rowIndex}-column-inlineEdit`}
        className="whitespace-nowrap pillartable-table-cell-general"
      >
        <div className="flex justify-center gap-x-1 break-words overflow-visible whitespace-normal">
          <div className="">
            <PillarForm.Button
              testid="edit-button"
              className="flex items-center justify-center gap-x-0.5 button-small-general-filled"
              onClick={(event) => {
                event.stopPropagation();
                setInlineEditMode("edit");
              }}
            >
              Edit
            </PillarForm.Button>
          </div>
        </div>
      </td>
    );
  }
  if (
    handleInlineEdit &&
    (inlineEditMode === "edit" || inlineEditMode === "saving")
  ) {
    return (
      <td
        data-testid={`${testidTable}-table-body-row-column-inlineEdit`}
        key={`row-${rowIndex}-column-inlineEdit`}
        className="whitespace-nowrap pillartable-table-cell-edit w-6 pr-1"
      >
        <PillarForm.Button
          className="button-small-neutral-filled mr-1 mb-1"
          testid="cancel-button"
          disabled={inlineEditMode === "saving"}
          onClick={(event) => {
            event.stopPropagation();
            setInlineEditMode("none");
          }}
        >
          Cancel
        </PillarForm.Button>
        <PillarForm.LoadingButton
          className="button-small-general-filled"
          testid="confirm-edit-button"
          disabled={inlineEditMode === "saving"}
          onClick={async (event) => {
            event.stopPropagation();
            let editResponse: Awaited<ReturnType<typeof handleInlineEdit>>;
            try {
              editResponse = await handleInlineEdit(rowValue, formValues);
            } catch (error) {
              // Show why in the row in place of these buttons. The row stays
              // in edit mode, so what the user typed is still there to fix
              // and confirm again once they dismiss the error.
              console.error(error);
              setRowActionFailure({
                action: "edit",
                error: toPillarApiError(error),
              });
              return;
            }
            if (
              editResponse &&
              typeof editResponse === "object" &&
              typeof editResponse !== "boolean" &&
              typeof editResponse !== "undefined"
            ) {
              setRowValue(editResponse);
            }
            if (typeof editResponse === "boolean" && editResponse) {
              await refetchData?.();
            }
            setInlineEditMode("none");
          }}
        >
          Confirm Edit
        </PillarForm.LoadingButton>
      </td>
    );
  }
  if (handleInlineEdit) {
    return (
      <td
        data-testid={`${testidTable}-table-body-row-column-inlineEdit`}
        key={`row-${rowIndex}-column-inlineEdit`}
        className="whitespace-nowrap pillartable-table-cell-edit w-6 pr-1"
      />
    );
  }
  return <></>;
};

export default PillarTableRowInlineEdit;
